import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Lock, Bell, BellOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    getEventMessages,
    isEventJoined,
    seedEventChatOnce,
    sendEventMessage,
    isMuted,
    toggleMute,
} from "@/lib/socialStore";
import { getEventById } from "@/lib/entitiesStore";
import { useSocialVersion, timeOfDay } from "@/lib/hooks";
import InitialAvatar from "@/components/InitialAvatar";
import { MOCK_PEOPLE } from "@/lib/mockData";

const EventChat = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { user } = useAuth();
    const v = useSocialVersion();
    const [draft, setDraft] = useState("");
    const scrollerRef = useRef(null);

    const event = useMemo(() => getEventById(id), [id]);
    const isHost = !!(user && event && event.ownerId === user.id);
    const joined = useMemo(
        () => (user && event ? (isHost || isEventJoined(user.id, event.id)) : false),
        [user, event, isHost, v]
    );
    const messages = useMemo(
        () => (event && joined ? getEventMessages(event.id) : []),
        [event, joined, v]
    );
    const muted = useMemo(
        () => (user && event ? isMuted(user.id, "event", event.id) : false),
        [user, event, v]
    );

    // Mock attendees: host (if any) + a few mock people + the user themselves
    const attendees = useMemo(() => {
        if (!event) return [];
        const list = [];
        if (event.hostName) {
            list.push({
                id: event.ownerId || "host",
                name: event.hostName,
                username: event.hostUsername || "host",
                role: "Host",
            });
        }
        if (user && (!event.ownerId || event.ownerId !== user.id)) {
            list.push({ id: user.id, name: user.fullName, username: user.username, role: "You" });
        }
        // Top up with deterministic mock attendees
        for (const p of MOCK_PEOPLE.slice(0, 5)) {
            if (!list.some((x) => x.username === p.username)) {
                list.push({ id: p.id, name: p.name, username: p.username, role: p.role });
            }
        }
        return list;
    }, [event, user]);

    useEffect(() => {
        if (event && joined) seedEventChatOnce(event.id, event);
    }, [event, joined]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages.length]);

    if (!event) {
        return (
            <div className="screen items-center justify-center" data-testid="event-chat-not-found">
                <p className="text-[14px] text-muted-foreground">Event not found.</p>
                <button onClick={() => navigate(-1)} className="mt-3 text-primary text-[13px] font-medium">
                    Back
                </button>
            </div>
        );
    }

    if (!joined) {
        return (
            <div className="screen" data-testid="event-chat-gate">
                <header className="px-3 pt-[max(12px,env(safe-area-inset-top))] pb-2 border-b border-border/60">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigate(-1)}
                            className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
                            aria-label="Back"
                            data-testid="event-chat-back-btn"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div className="min-w-0">
                            <h1 className="font-display text-[14.5px] font-semibold truncate">
                                {event.title}
                            </h1>
                            <p className="text-[11px] text-muted-foreground truncate">Event chat</p>
                        </div>
                    </div>
                </header>
                <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
                    <span className="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <Lock size={22} />
                    </span>
                    <h2 className="font-display text-[17px] font-semibold">
                        Join this event to access the event chat
                    </h2>
                    <p className="text-[13px] text-muted-foreground max-w-xs">
                        Only people who joined can see and send messages here.
                    </p>
                    <button
                        data-testid="event-chat-gate-join"
                        onClick={() => navigate(`/events/${event.id}`)}
                        className="h-12 px-6 rounded-full text-primary-foreground font-medium text-[14px] active:scale-[0.98]"
                        style={{
                            background:
                                "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                            boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                        }}
                    >
                        Go to event
                    </button>
                </div>
            </div>
        );
    }

    const onSend = () => {
        const txt = draft.trim();
        if (!txt) return;
        sendEventMessage(event.id, user, txt);
        setDraft("");
    };

    return (
        <div className="screen" data-testid="event-chat-screen">
            <header className="px-3 pt-[max(12px,env(safe-area-inset-top))] pb-2 border-b border-border/60">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
                        aria-label="Back"
                        data-testid="event-chat-back-btn"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <InitialAvatar name={event.title} size={36} />
                    <div className="min-w-0 flex-1">
                        <div className="font-display text-[14.5px] font-semibold truncate inline-flex items-center gap-1.5">
                            {event.title}
                            {muted && <BellOff size={12} className="text-muted-foreground" data-testid="event-chat-muted-icon" />}
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">
                            Event group chat · {event.hostName || "Host"}
                        </div>
                    </div>
                    <button
                        data-testid="event-chat-mute-btn"
                        onClick={() => toggleMute(user.id, "event", event.id)}
                        className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted active:scale-95 text-muted-foreground"
                        aria-label={muted ? "Unmute" : "Mute"}
                        aria-pressed={muted}
                        title={muted ? "Unmute" : "Mute"}
                    >
                        {muted ? <BellOff size={18} /> : <Bell size={18} />}
                    </button>
                </div>
            </header>

            {/* Attendees row — clickable */}
            <div
                className="px-3 pt-3 pb-2 border-b border-border/60 overflow-x-auto no-scrollbar"
                data-testid="event-chat-attendees"
            >
                <div className="flex items-center gap-3 min-w-max">
                    {attendees.map((a) => (
                        <button
                            key={a.username}
                            data-testid={`event-chat-attendee-${a.username}`}
                            onClick={() => navigate(`/u/${a.username}`)}
                            className="flex flex-col items-center gap-1 active:scale-95 transition-transform"
                            aria-label={`Open ${a.name}'s profile`}
                        >
                            <InitialAvatar name={a.name} size={42} />
                            <span className="text-[10.5px] font-medium text-foreground/80 max-w-[60px] truncate">
                                {a.name.split(" ")[0]}
                            </span>
                            {a.role && (
                                <span className="text-[9.5px] text-primary -mt-0.5">{a.role}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <div
                ref={scrollerRef}
                className="flex-1 overflow-y-auto no-scrollbar px-4 pt-4 pb-2 space-y-2"
                data-testid="event-chat-messages"
            >
                {messages.map((m, idx) => {
                    const mine = m.from === user.id;
                    const showTime = idx === 0 || m.at - messages[idx - 1].at > 5 * 60_000;
                    return (
                        <div key={m.id}>
                            {showTime && (
                                <div className="text-center text-[10.5px] text-muted-foreground my-2">
                                    {timeOfDay(m.at)}
                                </div>
                            )}
                            <div className={["flex", mine ? "justify-end" : "justify-start"].join(" ")}>
                                <div
                                    data-testid={mine ? "event-chat-bubble-me" : "event-chat-bubble-them"}
                                    className={[
                                        "max-w-[82%] px-3.5 py-2.5 rounded-2xl text-[14px] leading-snug",
                                        mine
                                            ? "bg-primary text-primary-foreground rounded-br-md shadow-soft"
                                            : "bg-secondary text-secondary-foreground rounded-bl-md",
                                    ].join(" ")}
                                >
                                    {!mine && (
                                        <div className="text-[10.5px] font-semibold text-primary mb-0.5">
                                            {m.fromName}
                                        </div>
                                    )}
                                    {m.text}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div className="h-2" />
            </div>

            <div className="px-3 pt-2 pb-[max(12px,env(safe-area-inset-bottom))] bg-background border-t border-border/60">
                <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center h-12 px-4 rounded-full bg-secondary focus-within:bg-background focus-within:ring-2 focus-within:ring-primary/40 transition-[background-color,box-shadow]">
                        <input
                            data-testid="event-chat-input"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && onSend()}
                            placeholder="Message the group…"
                            className="flex-1 bg-transparent outline-none text-[14px] placeholder:text-muted-foreground/80"
                        />
                    </div>
                    <button
                        data-testid="event-chat-send-btn"
                        onClick={onSend}
                        disabled={!draft.trim()}
                        className="h-12 w-12 flex items-center justify-center rounded-full text-primary-foreground shadow-card disabled:opacity-40 active:scale-95"
                        style={{
                            background:
                                "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        }}
                        aria-label="Send"
                    >
                        <Send size={18} strokeWidth={2.4} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EventChat;
